import React from 'react';
import {
    findVerticalScrollParent,
    HIDDEN_OVERLAY_STATE,
    isOverlayStateEqual,
    OverlayDescriptor,
    OverlayState,
    resolveGroupOverlay,
} from '../../hooks/group-overlay-shared';

/**
 * Minimal group identity required by mobile overlay hook.
 */
type GroupWithId = {
    id: string;
};

export type MobileOverlayState = OverlayState;

/**
 * Mobile overlay hook input.
 */
type Params = {
    groups: GroupWithId[];
    expandedGroups: Record<string, boolean>;
};

/**
 * Mobile overlay hook output refs/state/actions.
 */
type Result = {
    listRef: React.MutableRefObject<HTMLDivElement | null>;
    overlayRef: React.MutableRefObject<HTMLDivElement | null>;
    overlayState: MobileOverlayState;
    activeOverlayKeyRef: React.MutableRefObject<string | null>;
    setOverlayHidden: () => void;
    scheduleOverlayRecalculation: () => void;
    setGroupRootRef: (groupKey: string) => (node: HTMLDivElement | null) => void;
    setGroupHeaderRef: (groupKey: string) => (node: HTMLDivElement | null) => void;
};

export const useMobileGroupOverlay = ({ groups, expandedGroups }: Params): Result => {
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const overlayRef = React.useRef<HTMLDivElement | null>(null);
    const overlayRafRef = React.useRef<number | null>(null);
    const overlayScrollLoopRafRef = React.useRef<number | null>(null);
    const overlayLastScrollAtRef = React.useRef<number>(0);
    const scrollParentRef = React.useRef<HTMLElement | null>(null);
    const activeOverlayKeyRef = React.useRef<string | null>(null);
    const overlayHeightRef = React.useRef<number>(56);
    const groupRootRefsRef = React.useRef<Record<string, HTMLDivElement | null>>({});
    const groupHeaderRefsRef = React.useRef<Record<string, HTMLDivElement | null>>({});
    const [overlayState, setOverlayState] = React.useState<MobileOverlayState>(HIDDEN_OVERLAY_STATE);

    /**
     * Hides mobile overlay without extra diagnostics.
     */
    const setOverlayHidden = React.useCallback(() => {
        setOverlayState((state) => (state.activeKey === null ? state : HIDDEN_OVERLAY_STATE));
    }, []);

    /**
     * Tracks the current mobile overlay height to avoid measuring it on every scroll frame.
     */
    const updateOverlayHeight = React.useCallback(() => {
        const nextHeight = overlayRef.current?.getBoundingClientRect().height;
        if (nextHeight && nextHeight > 0) {
            overlayHeightRef.current = nextHeight;
        }
    }, []);

    /**
     * Recomputes active mobile overlay candidate from current list/group geometry.
     */
    const recalculateOverlay = React.useCallback(() => {
        const listElement = listRef.current;
        if (!listElement) {
            setOverlayHidden();
            return;
        }

        const scrollParent = scrollParentRef.current ?? findVerticalScrollParent(listElement);
        scrollParentRef.current = scrollParent;
        if (!scrollParent) {
            setOverlayHidden();
            return;
        }

        const stickyTop = scrollParent.getBoundingClientRect().top;
        const descriptors = groups
            .map((group) => {
                const root = groupRootRefsRef.current[group.id];
                const header = groupHeaderRefsRef.current[group.id];

                if (!root || !header) {
                    return null;
                }

                return {
                    key: group.id,
                    isOpen: expandedGroups[group.id] === true,
                    headerTop: header.getBoundingClientRect().top,
                    groupBottom: root.getBoundingClientRect().bottom
                };
            })
            .filter((descriptor): descriptor is OverlayDescriptor => descriptor !== null)
            .sort((left, right) => left.headerTop - right.headerTop);

        const {
            candidates,
            anchoredCandidates,
            activeCandidate,
        } = resolveGroupOverlay({
            descriptors,
            stickyTop,
            // First pass: quick candidate detection with cached overlay height.
            overlayHeight: overlayHeightRef.current,
        });

        if (candidates.length < 1) {
            setOverlayHidden();
            return;
        }

        if (anchoredCandidates.length < 1) {
            setOverlayHidden();
            return;
        }
        if (!activeCandidate) {
            setOverlayHidden();
            return;
        }
        const resolvedOverlayHeight = overlayHeightRef.current
            || groupHeaderRefsRef.current[activeCandidate.key]?.getBoundingClientRect().height
            || 56;
        const { offsetY } = resolveGroupOverlay({
            descriptors,
            stickyTop,
            // Second pass: use measured active header height for precise push-up behavior.
            overlayHeight: resolvedOverlayHeight,
        });

        const listRect = listElement.getBoundingClientRect();

        setOverlayState((state) => {
            const nextState: MobileOverlayState = {
                activeKey: activeCandidate.key,
                top: stickyTop,
                left: listRect.left,
                width: listRect.width,
                offsetY,
            };

            const isSameState = isOverlayStateEqual(state, nextState);

            return isSameState ? state : nextState;
        });
    }, [expandedGroups, groups, setOverlayHidden]);

    /**
     * Schedules one mobile recomputation per animation frame.
     */
    const scheduleOverlayRecalculation = React.useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (overlayRafRef.current !== null) {
            return;
        }

        overlayRafRef.current = window.requestAnimationFrame(() => {
            overlayRafRef.current = null;
            recalculateOverlay();
        });
    }, [recalculateOverlay]);

    /**
     * Keeps mobile overlay synchronized during momentum/inertial scroll.
     */
    const startOverlayScrollSyncLoop = React.useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (overlayScrollLoopRafRef.current !== null) {
            return;
        }

        const loop = () => {
            overlayScrollLoopRafRef.current = null;
            recalculateOverlay();

            const now = window.performance.now();
            if (now - overlayLastScrollAtRef.current < 140) {
                overlayScrollLoopRafRef.current = window.requestAnimationFrame(loop);
            }
        };

        overlayScrollLoopRafRef.current = window.requestAnimationFrame(loop);
    }, [recalculateOverlay]);

    /**
     * Stores/removes DOM root refs for each mobile group.
     */
    const setGroupRootRef = React.useCallback((groupKey: string) => (node: HTMLDivElement | null) => {
        if (node) {
            groupRootRefsRef.current[groupKey] = node;
            return;
        }

        delete groupRootRefsRef.current[groupKey];
    }, []);

    /**
     * Stores/removes DOM header refs for each mobile group.
     */
    const setGroupHeaderRef = React.useCallback((groupKey: string) => (node: HTMLDivElement | null) => {
        if (node) {
            groupHeaderRefsRef.current[groupKey] = node;
            return;
        }

        delete groupHeaderRefsRef.current[groupKey];
    }, []);

    React.useEffect(() => {
        activeOverlayKeyRef.current = overlayState.activeKey;
    }, [overlayState.activeKey]);

    React.useEffect(() => {
        if (groups.length < 1) {
            setOverlayHidden();
            return;
        }

        updateOverlayHeight();
        recalculateOverlay();
        scheduleOverlayRecalculation();
    }, [expandedGroups, groups, recalculateOverlay, scheduleOverlayRecalculation, setOverlayHidden, updateOverlayHeight]);

    React.useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) {
            return;
        }

        scrollParentRef.current = findVerticalScrollParent(listElement);
        const scrollParent = scrollParentRef.current;
        const handlePositionChange = () => {
            if (typeof window !== 'undefined') {
                overlayLastScrollAtRef.current = window.performance.now();
            }
            startOverlayScrollSyncLoop();
            scheduleOverlayRecalculation();
        };
        updateOverlayHeight();

        scrollParent?.addEventListener('scroll', handlePositionChange, { passive: true });
        window.addEventListener('scroll', handlePositionChange, { passive: true, capture: true });
        window.addEventListener('resize', handlePositionChange);

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                updateOverlayHeight();
                scheduleOverlayRecalculation();
            })
            : null;
        if (resizeObserver) {
            resizeObserver.observe(listElement);
            Object.values(groupRootRefsRef.current).forEach((node) => {
                if (node) {
                    resizeObserver.observe(node);
                }
            });
            if (overlayRef.current) {
                resizeObserver.observe(overlayRef.current);
            }
        }

        recalculateOverlay();
        scheduleOverlayRecalculation();

        return () => {
            scrollParent?.removeEventListener('scroll', handlePositionChange);
            window.removeEventListener('scroll', handlePositionChange, true);
            window.removeEventListener('resize', handlePositionChange);
            resizeObserver?.disconnect();
        };
    }, [groups, recalculateOverlay, scheduleOverlayRecalculation, startOverlayScrollSyncLoop, updateOverlayHeight]);

    React.useEffect(() => {
        return () => {
            if (overlayRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayRafRef.current);
                overlayRafRef.current = null;
            }
            if (overlayScrollLoopRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayScrollLoopRafRef.current);
                overlayScrollLoopRafRef.current = null;
            }
        };
    }, []);

    return {
        listRef,
        overlayRef,
        overlayState,
        activeOverlayKeyRef,
        setOverlayHidden,
        scheduleOverlayRecalculation,
        setGroupRootRef,
        setGroupHeaderRef,
    };
};
